import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { alertService } from '../services/alerts';

export const useAlerts = (status?: 'active' | 'acknowledged' | 'resolved') => {
  return useQuery({
    queryKey: ['alerts', status],
    queryFn: () => alertService.getAlerts(status),
    staleTime: 30 * 1000, // 30 seconds - alerts should be fresh
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time updates
  });
};

export const useAlertStats = () => {
  return useQuery({
    queryKey: ['alert-stats'],
    queryFn: () => alertService.getAlertStats(),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useAcknowledgeAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: alertService.acknowledgeAlert,

    onSuccess: (_data, _alertId) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      toast.success('Alert byl označen jako potvrzený');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Chyba při potvrzování alertu');
    },
  });
};

export const useAcknowledgeMultiple = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: alertService.acknowledgeMultiple,
    onSuccess: (_data, alertIds) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      toast.success(`${alertIds.length} alertů bylo označeno jako potvrzených`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Chyba při hromadném potvrzování alertů');
    },
  });
};

export const useClearOldAlerts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: alertService.clearOldAlerts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      toast.success('Staré alerty byly odstraněny');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Chyba při odstraňování starých alertů');
    },
  });
};

export const useResolveAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: alertService.resolveAlert,
    onSuccess: (_data, _alertId) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      toast.success('Alert byl označen jako vyřešený');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Chyba při řešení alertu');
    },
  });
};

export const useResolveMultiple = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: alertService.resolveMultiple,
    onSuccess: (_data, alertIds) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      toast.success(`${alertIds.length} alertů bylo označeno jako vyřešených`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Chyba při hromadném řešení alertů');
    },
  });
};
